import { useCallback } from 'react';
import { FormField, FormFields } from 'src/api/contentful/contentTypes.cloverdotcom';
import { ContactFormFieldProps, FormValues } from 'src/components/Contact/ContactFormTypes';
import { useValidations } from 'src/components/Contact/UseValidations';
import { getField } from 'src/utils/EntryUtil';
import { Environment } from 'src/utils/Environment';

type TContactForm = {
  getValidationSchema: () => {};
  getInitialValues: () => {};
};

export const DEFAULT_FORM_FIELDS: FormField[] = [
  { fieldType: FormFields.FIRST_AND_LAST_NAME },
  { fieldType: FormFields.EMAIL },
  { fieldType: FormFields.PHONE_NUMBER },
  { fieldType: FormFields.BUSINESS_NAME },
];

function formFieldsIncludesField(formFields: FormField[], fieldName: string) {
  return !!formFields.find(formField => formField.fieldType === fieldName);
}

function formFieldsIncludesFields(formFields: FormField[], ...fieldNames: string[]) {
  return fieldNames.some(fieldName => formFieldsIncludesField(formFields, fieldName));
}

function getSalesforceOwnerIdFromFormFields(formFields: FormField[]) {
  const predefinedField = formFields.find(formField => formField.fieldType === FormFields.HIDDEN_FIELD)?.predefinedField;
  const key = getField(predefinedField, 'key');
  if (key !== 'salesforceOwnerId') return;

  return Environment.current.isCloverProdEnvironment
    ? getField(predefinedField, 'productionValue')
    : getField(predefinedField, 'developmentValue');
}

export const useContactForm = (fields: ContactFormFieldProps = { formFields: DEFAULT_FORM_FIELDS }): TContactForm => {
  const formFields = fields && fields.formFields && fields.formFields.length
    ? fields.formFields
    : DEFAULT_FORM_FIELDS;

  // tslint:disable-next-line: cyclomatic-complexity
  const getValidationSchema = useCallback(() => {
    const {
      getFirstNameValidation,
      getLastNameValidation,
      getBusinessNameValidation,
      getEmailAddressValidation,
      getPhoneNumberValidation,
      getAddressValidation,
      getAddressOptionalValidation,
      getCityValidation,
      getStateValidation,
      getZipCodeValidation,
      getSsnLast4Validation,
      getBankAccountLast4Validation,
      getMidValidation,
      getBusinessTypeValidation,
      getYearsInBusinessValidation,
      getGoogleRecaptchaValidation,
      getRequiredCheckboxValidation,
      getNotRequiredValidation,
      getAnnualProcessingVolumeValidation,
      getWhenToCallValidation,
    } = useValidations(fields);

    return {
      firstName: formFieldsIncludesField(formFields, FormFields.FIRST_AND_LAST_NAME) ? getFirstNameValidation() : getNotRequiredValidation(),
      lastName: formFieldsIncludesField(formFields, FormFields.FIRST_AND_LAST_NAME) ? getLastNameValidation() : getNotRequiredValidation(),
      emailAddress: formFieldsIncludesField(formFields, FormFields.EMAIL) ? getEmailAddressValidation() : getNotRequiredValidation(),
      businessName: formFieldsIncludesField(formFields, FormFields.BUSINESS_NAME) ? getBusinessNameValidation() : getNotRequiredValidation(),
      phoneNumber: formFieldsIncludesField(formFields, FormFields.PHONE_NUMBER) ? getPhoneNumberValidation() : getNotRequiredValidation(),
      mobilePhoneNumber: formFieldsIncludesField(formFields, FormFields.MOBILE_PHONE_NUMBER) ? getPhoneNumberValidation() : getNotRequiredValidation(),
      businessAddressLine1: formFieldsIncludesField(formFields, FormFields.BUSINESS_ADDRESS) ? getAddressValidation() : getNotRequiredValidation(),
      addressLine2: formFieldsIncludesField(formFields, FormFields.BUSINESS_ADDRESS) ? getAddressOptionalValidation() : getNotRequiredValidation(),
      city: formFieldsIncludesField(formFields, FormFields.BUSINESS_ADDRESS) ? getCityValidation() : getNotRequiredValidation(),
      state: formFieldsIncludesField(formFields, FormFields.BUSINESS_ADDRESS) ? getStateValidation() : getNotRequiredValidation(),
      zipCode: formFieldsIncludesFields(formFields, FormFields.BUSINESS_ADDRESS, FormFields.ZIP_CODE) ? getZipCodeValidation() : getNotRequiredValidation(),
      ssnLast4: formFieldsIncludesField(formFields, FormFields.SSN_LAST_4) ? getSsnLast4Validation() : getNotRequiredValidation(),
      bankAccountLast4: formFieldsIncludesField(formFields, FormFields.BANK_ACCOUNT_LAST_4) ? getBankAccountLast4Validation() : getNotRequiredValidation(),
      mid: formFieldsIncludesField(formFields, FormFields.MID) ? getMidValidation() : getNotRequiredValidation(),
      businessType: formFieldsIncludesField(formFields, FormFields.BUSINESS_TYPE) ? getBusinessTypeValidation() : getNotRequiredValidation(),
      yearsInBusiness: formFieldsIncludesField(formFields, FormFields.YEARS_IN_BUSINESS) ? getYearsInBusinessValidation() : getNotRequiredValidation(),
      googleRecaptcha: formFieldsIncludesField(formFields, FormFields.GOOGLE_RECAPTCHA) ? getGoogleRecaptchaValidation() : getNotRequiredValidation(),
      requiredCheckbox: formFieldsIncludesField(formFields, FormFields.REQUIRED_CHECKBOX) ? getRequiredCheckboxValidation() : getNotRequiredValidation(),
      annualProcessingVolume: formFieldsIncludesField(formFields, FormFields.ANNUAL_PROCESSING_VOLUME) ? getAnnualProcessingVolumeValidation() : getNotRequiredValidation(),
      whenToCall: formFieldsIncludesField(formFields, FormFields.WHEN_TO_CALL) ? getWhenToCallValidation() : getNotRequiredValidation(),
    };
  }, [formFields]);

  const getInitialValues = () => {
    const {
      firstName,
      lastName,
      emailAddress,
      businessName,
      phoneNumber,
      mobilePhoneNumber,
      businessAddressLine1,
      addressLine2,
      city,
      state,
      zipCode,
      ssnLast4,
      bankAccountLast4,
      mid,
      businessType,
      yearsInBusiness,
      googleRecaptcha,
      requiredCheckbox,
      annualProcessingVolume,
      whenToCall,
    } = fields;

    // tslint:disable-next-line: cyclomatic-complexity
    const defaultInitialValues = {
      firstName: firstName || '',
      lastName: lastName || '',
      emailAddress: emailAddress || '',
      businessName: businessName || '',
      phoneNumber: phoneNumber || '',
      mobilePhoneNumber: mobilePhoneNumber || '',
      businessAddressLine1: businessAddressLine1 || '',
      addressLine2: addressLine2 || '',
      city: city || '',
      state: state || '',
      zipCode: zipCode || '',
      ssnLast4: ssnLast4 || '',
      bankAccountLast4: bankAccountLast4 || '',
      mid: mid || '',
      businessType: businessType || '',
      yearsInBusiness: yearsInBusiness || '',
      googleRecaptcha: googleRecaptcha || '',
      requiredCheckbox: requiredCheckbox || false,
      annualProcessingVolume: annualProcessingVolume || '',
      whenToCall: whenToCall || '',
      salesforceOwnerId: getSalesforceOwnerIdFromFormFields(formFields) || '',
    };

    if (!formFields || !formFields.length) {
      return defaultInitialValues;
    }

    return formFields.reduce((accum: FormValues, curr) => {
      switch (curr.fieldType) {
        case FormFields.FIRST_AND_LAST_NAME:
          accum.firstName = defaultInitialValues.firstName;
          accum.lastName = defaultInitialValues.lastName;
          break;
        case FormFields.BUSINESS_NAME:
          accum.businessName = defaultInitialValues.businessName;
          break;
        case FormFields.EMAIL:
          accum.emailAddress = defaultInitialValues.emailAddress;
          break;
        case FormFields.PHONE_NUMBER:
          accum.phoneNumber = defaultInitialValues.phoneNumber;
          break;
        case FormFields.MOBILE_PHONE_NUMBER:
          accum.mobilePhoneNumber = defaultInitialValues.mobilePhoneNumber;
          break;
        case FormFields.BUSINESS_ADDRESS:
          accum.businessAddressLine1 = defaultInitialValues.businessAddressLine1;
          accum.addressLine2 = defaultInitialValues.addressLine2;
          accum.city = defaultInitialValues.city;
          accum.state = defaultInitialValues.state;
          accum.zipCode = defaultInitialValues.zipCode;
          break;
        case FormFields.ZIP_CODE:
          accum.zipCode = defaultInitialValues.zipCode;
          break;
        case FormFields.SSN_LAST_4:
          accum.ssnLast4 = defaultInitialValues.ssnLast4;
          break;
        case FormFields.BANK_ACCOUNT_LAST_4:
          accum.bankAccountLast4 = defaultInitialValues.bankAccountLast4;
          break;
        case FormFields.MID:
          accum.mid = defaultInitialValues.mid;
          break;
        case FormFields.BUSINESS_TYPE:
          accum.businessType = defaultInitialValues.businessType;
          break;
        case FormFields.YEARS_IN_BUSINESS:
          accum.yearsInBusiness = defaultInitialValues.yearsInBusiness;
          break;
        case FormFields.GOOGLE_RECAPTCHA:
          accum.googleRecaptcha = defaultInitialValues.googleRecaptcha;
          break;
        case FormFields.REQUIRED_CHECKBOX:
          accum.requiredCheckbox = defaultInitialValues.requiredCheckbox;
          break;
        case FormFields.ANNUAL_PROCESSING_VOLUME:
          accum.annualProcessingVolume = defaultInitialValues.annualProcessingVolume;
          break;
        case FormFields.WHEN_TO_CALL:
          accum.whenToCall = defaultInitialValues.whenToCall;
        case FormFields.HIDDEN_FIELD:
          accum.salesforceOwnerId = defaultInitialValues.salesforceOwnerId;
      }
      return accum;
    }, {});
  };

  return {
    getInitialValues,
    getValidationSchema,
  };
};
